import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userManagementService, AdminUser } from '../services/userManagementService';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Convert AdminUser to User format
const convertToUser = (adminUser: AdminUser): User => ({
  id: adminUser._id,
  email: adminUser.email,
  name: adminUser.name,
  role: adminUser.role,
  permissions: adminUser.permissions
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for stored authentication on app load
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminToken');
      }
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Try API login first
      const result = await userManagementService.login(email, password);
      
      if (result && result.user) {
        const userData = convertToUser(result.user);
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('adminUser', JSON.stringify(userData));
        localStorage.setItem('adminToken', result.token);
        return true;
      }

      // Fallback to hardcoded admin for development
      if (email === 'admin@tak8.com' && password === 'admin123') {
        const userData: User = {
          id: '1',
          email: 'admin@tak8.com',
          name: 'Admin User',
          role: 'admin',
          permissions: [
            'view_dashboard',
            'view_analytics',
            'manage_fleet',
            'manage_bookings',
            'manage_content',
            'manage_blog',
            'manage_users',
            'manage_discounts',
            'manage_settings',
            'view_system_health',
            'manage_notifications'
          ]
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('adminUser', JSON.stringify(userData));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      
      // Fallback for when API is not available
      if (email === 'admin@tak8.com' && password === 'admin123') {
        const userData: User = {
          id: '1',
          email: 'admin@tak8.com',
          name: 'Admin User',
          role: 'admin',
          permissions: [
            'view_dashboard',
            'view_analytics',
            'manage_fleet',
            'manage_bookings',
            'manage_content',
            'manage_blog',
            'manage_users',
            'manage_discounts',
            'manage_settings',
            'view_system_health',
            'manage_notifications'
          ]
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('adminUser', JSON.stringify(userData));
        return true;
      }
      
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
  };

  const refreshUser = async () => {
    // Refresh user data from API if needed
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.id) {
          const freshUser = await userManagementService.getUser(userData.id);
          if (freshUser) {
            const converted = convertToUser(freshUser);
            setUser(converted);
            localStorage.setItem('adminUser', JSON.stringify(converted));
          }
        }
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  const hasPermission = (permission: string): boolean => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;
    return user?.permissions.includes(permission) || false;
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role || false;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    hasRole,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
